import { Card, Col, Flex, Row, Skeleton, Typography } from "antd"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { invoicesHiddenForRole } from "../../../../utils/invoices-visibility.ts"
import { type AppDispatch } from "../../../../store.ts"
import { ACTIVE_STATUSES } from "../../../Projects/constants/project-statuses.ts"
import { projectsSetGridFilters } from "../../../Projects/redux/projects.slice.ts"
import { type PermissionFeatures } from "../../../SettingsPermissions/types/permissions.types.ts"
import { fetchDashboardStats } from "../../redux/dashboard.thunks.ts"
import { type DashboardStats as DashboardStatsType } from "../../types/types.ts"
import {
  DASHBOARD_STATS_ITEMS,
  type DashboardStatItemConfig,
  type DashboardStatType,
  readStatValue
} from "./dashboard-stats-config.tsx"

const beforeNavigateDispatchActions: Partial<
  Record<DashboardStatType, Parameters<AppDispatch>[0]>
> = {
  projects: projectsSetGridFilters({
    project_status: ACTIVE_STATUSES.join(",")
  })
}

const { Text, Title } = Typography

interface DashboardStatsProps {
  permissionFeatures: PermissionFeatures
}

export const DashboardStats = memo(
  ({ permissionFeatures }: DashboardStatsProps) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
    const invoicesHidden = invoicesHiddenForRole(roleId)

    const [data, setData] = useState<DashboardStatsType | null>(null)
    const [loading, setLoading] = useState(false)

    const handleGetStats = useCallback(async () => {
      try {
        setLoading(true)
        const res = await dispatch(fetchDashboardStats()).unwrap()
        setData(res)
      } finally {
        setLoading(false)
      }
    }, [dispatch])

    const handleClick = function (stat: DashboardStatItemConfig) {
      const action = beforeNavigateDispatchActions?.[stat?.key]
      if (action) dispatch(action)
      navigate(stat?.href as string)
    }

    const visibleStats = useMemo(() => {
      if (!permissionFeatures) return []
      return DASHBOARD_STATS_ITEMS.filter(
        (item) =>
          item.isVisible(permissionFeatures) &&
          !(item.key === "invoices" && invoicesHidden)
      )
    }, [permissionFeatures, invoicesHidden])

    useEffect(() => {
      void handleGetStats()
    }, [handleGetStats])

    return (
      <Row gutter={[16, 16]}>
        {loading
          ? Array.from({ length: visibleStats.length }).map((_, i) => (
              <Col key={i} xs={24} sm={12} xl={6}>
                <Skeleton.Node className="w-full h-[81px]" />
              </Col>
            ))
          : visibleStats.map((stat) => (
              <Col key={stat.key} xs={24} sm={12} xl={6}>
                <Card
                  onClick={stat.href ? () => handleClick(stat) : undefined}
                  hoverable={!!stat.href}
                  variant="borderless"
                  classNames={{ body: "p-4" }}
                >
                  <Flex align="center" gap="middle">
                    <Flex
                      align="center"
                      justify="center"
                      className="size-12 rounded-card"
                      style={{
                        backgroundColor: stat.iconBg,
                        color: stat.iconColor
                      }}
                    >
                      {stat.icon}
                    </Flex>
                    <div>
                      <Title level={5}>{stat.title}</Title>
                      <Text
                        type="secondary"
                        data-testid={`stat-value-${stat.key}`}
                      >
                        {readStatValue(data, stat) ?? t("widget.empty", "No data")}
                      </Text>
                    </div>
                  </Flex>
                </Card>
              </Col>
            ))}
      </Row>
    )
  }
)

DashboardStats.displayName = "DashboardStats"
